/**
 * OpenTable OAuth Authorization Page
 *
 * This component handles the OAuth authorization flow for OpenTable integration.
 * Users are redirected here from OpenTable with query parameters, select a restaurant,
 * and authorize the integration which redirects them back to OpenTable.
 *
 * Flow:
 * 1. User is redirected from OpenTable with query params (client_id, redirect_uri, state)
 * 2. On page load, we call /api/opentable/start-auth to initialize the session
 * 3. Backend returns available stores and a session token
 * 4. User selects a restaurant and clicks "Authorize"
 * 5. We call /api/opentable/authorize with the session token and store ID
 * 6. Backend returns a redirect URL which we use to send user back to OpenTable
 *
 * @route /opentable/oauth/authorize
 * @requires Authentication - redirects to sign-in if not authenticated
 * @requires Query params: client_id, redirect_uri, state
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import {
  Button,
  Callout,
  Field,
  IconArrowRight,
  Select,
  Spinner,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';

const PERMISSIONS = [
  'Search restaurant availability',
  'Retrieve dining experiences information',
  'Lock reservation slots',
  'Make reservations (standard and experiences)',
  'Retrieve cancellation and booking policies',
  'Cancel reservations',
  'Lookup diners by phone number',
  'Change reservation details',
];

interface Store {
  id: number;
  name: string;
}

export default function OpenTable() {
  const { accessToken, user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Query params from OpenTable OAuth redirect
  // These are required and validated before making any backend calls
  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const stateParam = searchParams.get('state'); // Raw JSON string from OpenTable, URL-encoded

  // State from backend after calling /api/opentable/start-auth
  const [sessionToken, setSessionToken] = useState<string | null>(null); // Used for authorization
  const [rid, setRid] = useState<string | null>(null); // Optional, for display/debugging
  const [stores, setStores] = useState<Store[]>([]); // Available stores user can select
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null); // Selected restaurant/store

  // UI state
  const [loading, setLoading] = useState(true); // Initial page load / start-auth call
  const [isAuthorizing, setIsAuthorizing] = useState(false); // During authorization call
  const [error, setError] = useState<string>(''); // Error message display

  // Redirect to sign in if not authenticated
  useEffect(() => {
    if (!authLoading && !accessToken) {
      navigate('/signin', {
        state: {
          from: location.pathname + location.search,
          message: 'Please sign in to connect with OpenTable.',
        },
      });
    }
  }, [accessToken, authLoading, navigate, location]);

  /**
   * Step 1: On page load, initialize the OAuth session
   *
   * This effect runs when the component mounts and:
   * 1. Validates required query parameters are present
   * 2. Calls POST /api/opentable/start-auth with the OAuth params
   * 3. Saves the session token and available stores from the response
   * 4. Defaults to the first store if available
   */
  useEffect(() => {
    const startAuth = async () => {
      // Validate required query params from OpenTable
      if (!clientId || !redirectUri || !stateParam) {
        setError(
          'Missing required parameters. Please access this page through OpenTable.',
        );
        setLoading(false);
        return;
      }

      // Ensure user is authenticated (will be handled by redirect effect if not)
      if (!accessToken || !user) {
        return;
      }

      setLoading(true);
      setError('');

      try {
        // Call backend to initialize the OAuth session
        // Body includes all required OAuth params from OpenTable
        const response = await api.post(apiEndpoints.openTableStartAuth(), {
          client_id: clientId,
          redirect_uri: redirectUri,
          state: stateParam, // Raw JSON string, URL-encoded
        });

        if (response && response.ok) {
          // Expected 200 response:
          // {
          //   "session_token": "abc123",
          //   "rid": "1234", // optional
          //   "available_stores": [
          //     { "id": 1, "name": "Kupfert & Kim - Spadina" },
          //     { "id": 2, "name": "Kupfert & Kim - York" }
          //   ]
          // }
          const data = await response.json();
          setSessionToken(data.session_token); // Required for authorization step
          if (data.rid) {
            setRid(data.rid); // Optional, for display
          }
          if (data.available_stores && Array.isArray(data.available_stores)) {
            setStores(data.available_stores);
            // Default to first store in the list
            if (data.available_stores.length > 0) {
              setSelectedStoreId(data.available_stores[0].id);
            }
          }
        } else {
          // Handle non-200 responses
          let errorMessage =
            "We couldn't start the OpenTable connection. Please try again or contact support.";
          if (response) {
            try {
              const errorData = await response.json();
              if (errorData.error) {
                errorMessage = `Error: ${errorData.error}`;
              } else if (errorData.detail || errorData.message) {
                errorMessage = errorData.detail || errorData.message;
              }
            } catch (e) {
              // If we can't parse error, use default message
            }
          }
          setError(errorMessage);
        }
      } catch (err: any) {
        console.error('Failed to start OpenTable auth:', err);
        setError(
          "We couldn't start the OpenTable connection. Please try again or contact support.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (accessToken && user) {
      startAuth();
    }
  }, [accessToken, user, clientId, redirectUri, stateParam]);

  /**
   * Step 2: Handle Authorize button click
   *
   * When user clicks "Authorize":
   * 1. Validates a store is selected and session token exists
   * 2. Calls POST /api/opentable/authorize with session_token and store_id
   * 3. On success, redirects to the redirect_url returned by backend
   * 4. On error, shows error message without redirecting
   */
  const handleAuthorize = async () => {
    // Validate store selection
    if (!selectedStoreId) {
      setError('Please select a restaurant.');
      return;
    }

    // Validate session token exists
    if (!sessionToken) {
      setError('Session expired. Please refresh the page and try again.');
      return;
    }

    setIsAuthorizing(true);
    setError('');

    try {
      // Call backend to complete authorization
      // Body includes session token from step 1 and selected store ID
      const response = await api.post(apiEndpoints.openTableAuthorize(), {
        session_token: sessionToken,
        store_id: selectedStoreId,
      });

      if (response && response.ok) {
        // Expected 200 response:
        // {
        //   "redirect_url": "https://gc-pp-rs.otenv.com/redirect?code=<code>&state=<state>"
        // }
        const data = await response.json();
        if (data.redirect_url) {
          // Step 2.1: Immediately redirect to OpenTable with authorization code
          // Use exactly what backend returns, no extra query params
          window.location.href = data.redirect_url;
        } else {
          setError(
            'Invalid response from server. Please try again or contact support.',
          );
          setIsAuthorizing(false);
        }
      } else {
        // Step 2.2: Handle non-200 response - show error without redirecting
        let errorMessage =
          "We couldn't complete the connection to OpenTable. Please try again.";
        if (response) {
          try {
            const errorData = await response.json();
            // Try to extract meaningful error message from response
            if (errorData.error) {
              errorMessage = `Error: ${errorData.error}`;
            } else if (errorData.detail || errorData.message) {
              errorMessage = errorData.detail || errorData.message;
            }
          } catch (e) {
            // If we can't parse error JSON, use default message
          }
        }
        setError(errorMessage);
        setIsAuthorizing(false);
      }
    } catch (err: any) {
      // Handle network errors or other exceptions
      console.error('Failed to authorize OpenTable:', err);
      setError(
        "We couldn't complete the connection to OpenTable. Please try again.",
      );
      setIsAuthorizing(false);
    }
  };

  /**
   * Step 3: Handle Deny button click
   *
   * If user clicks "Deny", we don't call the backend (OpenTable doesn't
   * define a callback for denial). Just redirect to dashboard home.
   */
  const handleDeny = () => {
    navigate('/');
  };

  /**
   * Step 4: Handle Sign Out
   *
   * Signs the user out and redirects to the sign-in page.
   * No special OpenTable behavior needed - just standard logout flow.
   */
  const handleSignOut = () => {
    logout(); // Clears auth tokens and user data
    navigate('/signin');
  };

  if (authLoading || loading) {
    return (
      <div
        role="status"
        className="av2 flex min-h-screen items-center justify-center gap-2 text-v2-13 text-v2-fg-3"
      >
        <Spinner />
        Loading...
      </div>
    );
  }

  if (!accessToken || !user) {
    return null; // Will redirect
  }

  return (
    <>
      <PageMeta
        title="Connect OpenTable | Ask Arthur"
        description="Connect your restaurant with OpenTable integration"
      />
      <div className="av2 flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-[560px] rounded-v2-lg border border-v2-line bg-v2-surface p-6 sm:p-8">
          <div className="mb-6 flex items-center justify-center gap-5">
            <img
              src="/images/logo/logo-full.svg"
              alt="Ask Arthur"
              className="h-12 w-auto max-w-[160px]"
            />
            <IconArrowRight size={18} className="text-v2-fg-4" />
            <img
              src="/images/logo/opentable-logo.svg"
              alt="OpenTable"
              className="h-9 w-auto"
            />
          </div>

          <h1 className="text-center text-v2-20 font-semibold text-v2-fg">
            Allow <strong>AskArthur</strong> to integrate with{' '}
            <strong>OpenTable</strong>?
          </h1>

          <div className="mt-6">
            <p className="text-v2-13 text-v2-fg-2">
              This application will be able to:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-v2-13 text-v2-fg">
              {PERMISSIONS.map((permission) => (
                <li key={permission}>{permission}</li>
              ))}
            </ul>
          </div>

          <div className="mt-6">
            <p className="mb-3 text-v2-13 text-v2-fg-2">
              for the following location:
            </p>
            <Field label="Restaurant">
              {(id) => (
                <Select
                  id={id}
                  value={selectedStoreId || ''}
                  onChange={(e) =>
                    setSelectedStoreId(
                      e.target.value ? parseInt(e.target.value) : null,
                    )
                  }
                >
                  <option value="">Select a restaurant</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          {error && (
            <Callout tone="danger" className="mt-4">
              {error}
            </Callout>
          )}

          <div className="mt-6 flex flex-col gap-2">
            <Button
              variant="primary"
              className="w-full"
              onClick={handleAuthorize}
              loading={isAuthorizing}
              disabledReason={
                !sessionToken
                  ? 'The OpenTable session could not be started.'
                  : !selectedStoreId
                    ? 'Select a restaurant to authorize.'
                    : undefined
              }
            >
              {isAuthorizing ? 'Authorizing...' : 'Authorize'}
            </Button>
            <Button
              className="w-full"
              onClick={handleDeny}
              disabled={isAuthorizing}
            >
              Deny
            </Button>
            <div className="mt-1 text-center">
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
